import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Title,
  Transition,
  Notification,
  useMantineTheme,
} from '@mantine/core';
import useRingCalculator from '../../Hooks/use-ring-calculator';
import * as d3 from 'd3';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../../Components/Tooltip';
import { Chance } from 'chance';
import { DEFAULT_BLIP_COLOR } from '../Technology/Form';
import { generateSeed, getClassSafeName, groupBy, isColliding, pickTextColorBasedOnBackgroundColor } from './util';
import SectionTechnologyList from '../../Components/SectionTechnologyList';
import useRingColors from '../../Hooks/use-ring-colors';
import { Technology } from '../../../backend/models/technology';
import { Ring } from '../../../backend/models/ring';
import { Section } from '../../../backend/models/section';
import { Person } from '../../../backend/models/person';

type RadarType = {
  technologies: Technology[];
  rings: Ring[];
  sections: Section[];
  persons: Person[];
};

type Datum = {
  name: string;
  value: number;
  index: number;
};

export const SELECTED_BLIP_WIDTH = 22;
const DEFAULT_BLIP_WIDTH = 8;
const SELECTED_MIN_BLIP_WIDTH = 15;
const DEFAULT_MIN_BLIP_WIDTH = 6;
const MAX_ITERATIONS = 100;
const CLICKED_SECTION_SCALE = 1.1;
const FULL_SCREEN_HINT = 'fullScreenHint';
const CLICK_TO_EXPAND_HINT = 'clickToExpandHint';

type TechnologyWithSectionAndRing = Omit<Technology, 'section' | 'ring'> & {
  // All blips in the radar have a section and a ring
  section: Section;
  ring: Ring;
};

export type Blip = TechnologyWithSectionAndRing & {
  x: number;
  y: number;
  width: number;
  number: number;
};

function Radar(props: RadarType) {
  const [blips, setBlips] = useState<Array<Blip>>([]);
  const [hoveredBlip, setHoveredBlip] = useState<Blip | null>(null);
  const [sectionClicked, setSectionClicked] = useState<string | null>(null);
  const [fullScreenHintClicked, setFullScreenHintClicked] = useState<boolean>(
    sessionStorage.getItem(FULL_SCREEN_HINT) === 'true',
  );
  const [clickToExpandHintClicked, setClickToExpandHintClicked] = useState<boolean>(
    sessionStorage.getItem(CLICK_TO_EXPAND_HINT) === 'true',
  );
  const hasDrawn = useRef<boolean>(false);
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { ringColors } = useRingColors();
  const defaultSpacing = theme.spacing.md;

  const sectionNames = useMemo(
    () => [
      ...new Set(
        props.sections
          .map((section) => section.name)
          .filter((s) => props.technologies.some((d) => d.section && d.section.name === s)),
      ),
    ],
    [props.sections, props.technologies],
  );
  const ringNames = useMemo(
    () => [
      ...new Set(
        props.rings.map((ring) => ring.name).filter((r) => props.technologies.some((d) => d.ring && d.ring.name === r)),
      ),
    ],
    [props.rings, props.technologies],
  );
  const data = useMemo(() => {
    const focusedSection = Math.max(1 / 4, 1 / sectionNames.length);
    return sectionNames.map((name, i) => {
      return {
        name,
        value: sectionClicked === name ? focusedSection : (1 - focusedSection) / (sectionNames.length - 1),
        index: i,
      };
    });
  }, [sectionNames, sectionClicked]);

  const arcData = d3
    .pie<Datum>()
    .startAngle(-0.5 * Math.PI)
    .endAngle(1.5 * Math.PI)
    .sort(null)
    .value((d) => d.value)(data);

  useEffect(() => {
    const formattedData: Array<Blip> = props.technologies
      .filter((d) => d.ring && d.section && ringNames.includes(d.ring.name) && sectionNames.includes(d.section.name))
      .map((newData, index) => ({
        ...(newData as TechnologyWithSectionAndRing),
        x: 0,
        y: 0,
        width: DEFAULT_BLIP_WIDTH,
        number: index + 1,
      }));

    setBlips(formattedData);
  }, [props.technologies, ringNames, sectionNames]);

  // radar target margins + paddings
  const staticWidths = 400 + defaultSpacing * 4;

  // max width for radar should be window width - min-width for section list - statics
  const size = Math.min(window.innerHeight - 200, window.innerWidth - 300 - staticWidths);
  const sectionSize = size / 2 - defaultSpacing * 2;

  const getBlipOptimalWidth = useCallback(
    (b: Blip) => {
      return b.section.name === sectionClicked ? SELECTED_BLIP_WIDTH : DEFAULT_BLIP_WIDTH;
    },
    [sectionClicked],
  );

  const getBlipMinWidth = useCallback(
    (b: Blip) => {
      return b.section.name === sectionClicked ? SELECTED_MIN_BLIP_WIDTH : DEFAULT_MIN_BLIP_WIDTH;
    },
    [sectionClicked],
  );

  const selectedSectionGroupedByRingId = groupBy(
    blips.filter((d) => d.section.name === sectionClicked),
    'ringId',
  );

  const { getRadius } = useRingCalculator(ringNames.length, sectionSize);

  const unsetClickedSection = useCallback(() => {
    hasDrawn.current = false;
    d3.selectAll('.arc').style('opacity', 1);
    d3.selectAll('.arc').style('transform', 'scale(1)');
    const arr = [...blips];
    arr.map((a) => {
      a.width = DEFAULT_BLIP_WIDTH;
      return a;
    });
    setBlips(arr);
    setSectionClicked(null);
  }, [blips]);

  const clickSection = useCallback(
    (section: string) => {
      if (section === sectionClicked) {
        unsetClickedSection();
        return;
      }
      d3.selectAll(`.section-arc-${getClassSafeName(section)}`)
        .style('opacity', 1)
        .style('transform', `scale(${CLICKED_SECTION_SCALE})`);
      d3.selectAll(`.arc:not(.section-arc-${getClassSafeName(section)})`)
        .style('opacity', 0.5)
        .style('transform', 'scale(1)');
      hasDrawn.current = false;
      const arr = [...blips];
      arr.map((a) => {
        a.width = a.section.name === section ? SELECTED_BLIP_WIDTH : DEFAULT_BLIP_WIDTH;
        return a;
      });
      setBlips(arr);
      setSectionClicked(section);
    },
    [blips, sectionClicked, unsetClickedSection],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        unsetClickedSection();
      }
    },
    [unsetClickedSection],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  /**
   * This function uses `chance` with a "seed" (see `generateSeed`) guaranteeing repeatable
   * randomeness - meaning the blips won't jump around on refresh as long as we don't
   * add/remove/move blips.
   *
   * The radius (distance from center) will be randomized within `minRadius` and `maxRadius` if we
   * haven't set a `radialAdjust` for the blip. If we have - we will use a normal distribition
   * from around `radialAdjust` / 100% of the allowed radius (accounting for deviation).
   *
   * The angle will be randomized within the arc's (cake piece) angles, accounting for the width
   * of a blip. This means we don't place the center of the blip on the absolute edge of an arc
   * since that would mean half the blip would be rendered outside of it.
   */
  function calculateBlipCoordinates(
    b: Blip,
    chance: Chance.Chance,
    minRadius: number,
    maxRadius: number,
    arcDataForSection: d3.PieArcDatum<Datum>,
  ) {
    let radius;
    // Check for both undefined (never set) and null (set to random)
    if (b.radialAdjust === null || b.radialAdjust === undefined) {
      radius = chance.floating({ min: minRadius, max: maxRadius });
    } else {
      const ringRadius = maxRadius - minRadius;
      const deviation = ringRadius * 0.1;
      // |X--------X| adjusting center of normal distribution to avoid technologies
      // landing outside ring (X being mean values not allowed)
      const adjustableRadius = ringRadius * 0.8;
      const mean = minRadius + deviation + (adjustableRadius * b.radialAdjust) / 100;
      radius = chance.normal({ mean, deviation });
    }

    const blipWidthAngleCompensation = Math.min(
      (arcDataForSection.endAngle - arcDataForSection.startAngle) / 2,
      Math.atan2(b.width, radius),
    );

    const angle =
      chance.floating({
        min: arcDataForSection.startAngle + blipWidthAngleCompensation,
        max: arcDataForSection.endAngle - blipWidthAngleCompensation,
      }) -
      Math.PI / 2;

    b.x = radius * Math.cos(angle);
    b.y = radius * Math.sin(angle);
  }

  /**
   *  This function will call the `calculateBlipCoordinates` 100 times, trying to find a place
   * on the radar where it doesn't collide with other blips. If it fails to do so, it will return
   * false and findBlipCoordinates will adjust blip properties and call this function again.
   */
  const tryGetBlipPlacement = useCallback(
    (
      b: Blip,
      chance: Chance.Chance,
      minRadius: number,
      maxRadius: number,
      arcDataForSection: d3.PieArcDatum<Datum>,
      drawnCoordinates: { x: number; y: number }[],
    ): boolean => {
      let iterationCounter = 0;

      calculateBlipCoordinates(b, chance, minRadius, maxRadius, arcDataForSection);

      while (iterationCounter <= MAX_ITERATIONS) {
        if (isColliding(b, drawnCoordinates)) {
          calculateBlipCoordinates(b, chance, minRadius, maxRadius, arcDataForSection);
        } else {
          return true;
        }
        iterationCounter++;
      }

      return false;
    },
    [],
  );

  /**
   *  This function will start by trying to place the blip in the radar without modifications.
   *
   *  If it collides with another blip already drawn it will try to decrease the width of the blip.
   *
   *  If that doesn't help it will try to move the blip further out from the center and reset the blip
   *  width to `OPTIMAL_BLIP_WIDTH` and repeat the process of shinking it in the new placement.
   *
   *  This is repeated till we reach a `radialAdjust` of 100.
   *  Then, as a last resort, it will set the `radialAdjust` to undefined - meaning it can be placed anywhere.
   *  This helps if there's too many blips with '100' for example - it will place the blip closer to the center.
   */
  const findBlipCoordinates = useCallback(
    (
      b: Blip,
      chance: Chance.Chance,
      minRadius: number,
      maxRadius: number,
      arcDataForSection: d3.PieArcDatum<Datum>,
      drawnCoordinates: { x: number; y: number }[],
    ): void => {
      let foundAPlace = tryGetBlipPlacement(b, chance, minRadius, maxRadius, arcDataForSection, drawnCoordinates);

      if (foundAPlace) {
        drawnCoordinates.push({ x: b.x, y: b.y });
        return;
      }

      if (b.width > getBlipMinWidth(b)) {
        b.width = b.width - 1;
        return findBlipCoordinates(b, chance, minRadius, maxRadius, arcDataForSection, drawnCoordinates);
      }

      if (b.radialAdjust != null) {
        b.width = getBlipOptimalWidth(b);
        b.radialAdjust = b.radialAdjust <= 75 ? b.radialAdjust + 25 : undefined;
        return findBlipCoordinates(b, chance, minRadius, maxRadius, arcDataForSection, drawnCoordinates);
      }

      drawnCoordinates.push({ x: b.x, y: b.y });
    },
    [tryGetBlipPlacement, getBlipOptimalWidth, getBlipMinWidth],
  );

  const draw = useCallback(() => {
    const drawnCoordinates: { x: number; y: number }[] = [];
    d3.select('.section-texts').selectAll('g').remove();
    d3.select('.blips').selectAll('g').remove();

    ringNames.forEach((r, i) => {
      const innerRadius = getRadius(i);
      const outerRadius = getRadius(i + 1);

      const minRadius = Math.max(20, innerRadius) + SELECTED_BLIP_WIDTH / 2 + 10;
      const maxRadius = outerRadius - SELECTED_BLIP_WIDTH / 2;

      const arcGenerator = d3
        .arc()
        .innerRadius(innerRadius + 10)
        .outerRadius(outerRadius)
        .padAngle(0.05 / (i + 1));

      // Draw rings
      d3.select(`.ring-${getClassSafeName(r)}`)
        .selectAll('path')
        .data(arcData)
        .join('path')
        .attr('class', (d) => {
          return `arc section-arc-${getClassSafeName(d.data.name)}`;
        })
        .style('cursor', 'pointer')
        .on('click', (e, d) => clickSection(d.data.name))
        .attr('d', arcGenerator as any);

      // Order blips by technologies that has _any_ radial adjustment set first
      const blipsInRing = blips
        .filter((b) => b.ring.name === r)
        .sort((a, b) => (a.radialAdjust ?? 150) - (b.radialAdjust ?? 150));

      blipsInRing.forEach((b) => {
        const arcDataForSection = arcData.find((d) => d.data.name === b.section.name);
        if (arcDataForSection === undefined) return;

        const chance = new Chance(generateSeed(r, b.section.name, b.name));
        findBlipCoordinates(b, chance, minRadius, maxRadius, arcDataForSection, drawnCoordinates);

        // Add group element for blip
        const group = d3
          .select('.blips')
          .append('g')
          .style('transform', `translate(${b.x}px, ${b.y}px)`)
          .style('-moz-transform', `translate(${b.x}px, ${b.y}px)`)
          .style('cursor', 'pointer')
          .style('scale', b.section.name === sectionClicked ? CLICKED_SECTION_SCALE : 1)
          .on('mouseenter', () => setHoveredBlip(b))
          .on('mouseleave', () => setHoveredBlip(null))
          .on('click', () => navigate(`/technology/${encodeURIComponent(b.name)}`));

        // Draw circle in blip group
        group
          .append('circle')
          .style('fill', b.blipColor ?? DEFAULT_BLIP_COLOR)
          .style('stroke', 'black')
          .style('stroke-width', '2px')
          .style('paint-order', 'stroke')
          .attr('r', b.width / (b.section.name === sectionClicked ? 2.2 : 2))
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('class', `arc section-arc-${getClassSafeName(b.section.name)}`);

        // Draw text in circle
        if (b.section.name === sectionClicked) {
          group
            .append('text')
            .attr('x', 0)
            .attr('y', 4)
            .style('font-size', (SELECTED_BLIP_WIDTH * 10) / 22 + 'px')
            .style('fill', pickTextColorBasedOnBackgroundColor(b.blipColor ?? DEFAULT_BLIP_COLOR, 'white', 'black'))
            .attr('text-anchor', 'middle')
            .attr('class', `arc section-arc-${getClassSafeName(b.section.name)}`)
            .text(b.number);
        }
      });
    });

    const radarElementCoords = (d3.select('.radar').node() as Element).getBoundingClientRect();

    sectionNames.forEach((s, i) => {
      const arcDataForSection = arcData.find((d) => d.data.name === s);

      if (arcDataForSection) {
        const radius = getRadius(ringNames.length) + (s === sectionClicked ? 80 : 50);
        const angle = (arcDataForSection.startAngle + arcDataForSection.endAngle) / 2 - Math.PI * 0.5;
        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);

        // Add a group element for section name
        const sectionGroup = d3
          .select('.section-texts')
          .append('g')
          .style('cursor', 'pointer')
          .on('click', () => clickSection(s));

        // Draw section name in group
        const textElement = sectionGroup
          .append('text')
          .attr('x', x)
          .attr('y', y)
          .style('fill', 'black')
          .style('font-size', '16px')
          .style('font-weight', 500)
          .attr('text-anchor', Math.abs(x) < sectionSize / 3 ? 'middle' : x > 0 ? 'start' : 'end')
          .text(s);

        // Draw a > next to the section name
        const sectionTextClientRect = textElement.node()?.getBoundingClientRect();

        const left = (sectionTextClientRect?.left ?? 0) - radarElementCoords.left - size / 2;
        const top = (sectionTextClientRect?.top ?? 0) - radarElementCoords.top - size / 2;
        const width = sectionTextClientRect?.width ?? 0;

        sectionGroup
          .append('polygon')
          .attr('points', '5.2105e-4 11.753 1.2874 13 8 6.505 1.2879 0 0 1.2461 5.4253 6.504')
          .attr('transform', `translate(${left + width + 5}, ${top + 5})`)
          .style('fill', 'black');
      }
    });

    setBlips(blips);
  }, [
    ringNames,
    sectionNames,
    blips,
    getRadius,
    arcData,
    clickSection,
    findBlipCoordinates,
    sectionClicked,
    navigate,
    sectionSize,
    size,
  ]);

  useEffect(() => {
    if (blips.length === 0 || hasDrawn.current) {
      return;
    }

    draw();
    hasDrawn.current = true;
  }, [blips.length, draw]);

  return (
    <Flex justify={sectionClicked ? 'space-between' : 'flex-end'} w={'100%'}>
      <Transition
        mounted={sectionClicked !== null}
        transition="slide-right"
        duration={300}
        timingFunction="ease-in-out"
      >
        {(styles) => (
          <Paper withBorder shadow={'md'} p={'lg'} pl={32} ml={-34} style={styles} sx={{ zIndex: 1 }}>
            <Flex justify={'space-between'} align={'center'} mb={'lg'}>
              <Title order={2} truncate>
                {sectionClicked}
              </Title>
              <CloseButton size={'xl'} onClick={() => unsetClickedSection()} />
            </Flex>
            <ScrollArea
              style={{ height: size - 100 }}
              w={window.innerWidth - size - staticWidths}
              miw={300}
              maw={600}
              // parent - title height - y-padding
              mah={'calc(100% - 64px - 40px)'}
              scrollbarSize={8}
              offsetScrollbars
              type={'auto'}
            >
              <SectionTechnologyList
                groupedResult={selectedSectionGroupedByRingId}
                onMouseOver={(blip) => setHoveredBlip(blip)}
                onMouseLeave={() => setHoveredBlip(null)}
                rings={props.rings}
                persons={props.persons}
              />
            </ScrollArea>
          </Paper>
        )}
      </Transition>
      <Box
        mr={200}
        ml={'auto'}
        pos={'relative'}
        style={{
          // |----------window.innerWidth---------|
          // |XXXXXXXXXXXXXXradar(size)margin(200)|
          // divide XXXXX by 2 and add 200 to account for margin-right of radar element
          transform: `translateX(${sectionClicked ? 0 : (window.innerWidth - defaultSpacing * 4 - size) / -2 + 200}px)`,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <svg className="radar" width={size} height={size} style={{ overflow: 'visible' }}>
          {ringNames.map((r, i) => {
            return (
              <Box
                key={r}
                component="g"
                className={`ring-${getClassSafeName(r)}`}
                sx={{ fill: ringColors[i] }}
                transform={`translate(${size / 2}, ${size / 2})`}
              />
            );
          })}
          <Box component="g" className={'blips'} transform={`translate(${size / 2}, ${size / 2})`} />
          <Box component="g" className={'section-texts'} transform={`translate(${size / 2}, ${size / 2})`} />
        </svg>
        {hoveredBlip && (
          <div
            style={{
              position: 'absolute',
              top: size / 2,
              left: size / 2,
            }}
          >
            <Tooltip
              blip={hoveredBlip}
              scale={hoveredBlip.section.name === sectionClicked ? CLICKED_SECTION_SCALE : 1}
            />
          </div>
        )}
      </Box>
      <Paper
        p={'sm'}
        sx={(theme) => ({
          position: 'absolute',
          left: 50,
          bottom: 50,
          fontSize: 14,
          fontWeight: 500,
          opacity: sectionClicked ? 0 : 1,
          transition: 'opacity 0.3s linear',
        })}
        shadow={'sm'}
        withBorder
      >
        <Flex gap={'sm'} direction={'column'}>
          {ringNames.map((ringName, i) => {
            return (
              <Group key={ringName}>
                <div style={{ width: 40, height: 25, backgroundColor: ringColors[i], borderRadius: 4 }} />
                {ringName}
              </Group>
            );
          })}
        </Flex>
      </Paper>
      <Flex
        direction={'column'}
        gap={'md'}
        sx={(theme) => ({
          position: 'absolute',
          left: 50,
          top: 110,
          fontSize: 16,
          opacity: sectionClicked ? 0 : 1,
          transition: 'opacity 0.3s linear',
        })}
        maw={300}
      >
        {!fullScreenHintClicked && (
          <Notification
            color={'avensia-turqoise'}
            onClose={() => {
              setFullScreenHintClicked(true);
              sessionStorage.setItem('fullScreenHint', 'true');
            }}
          >
            <span>The radar works best in full screen on screens with at least full HD resolution</span>
          </Notification>
        )}
        {!clickToExpandHintClicked && (
          <Notification
            color={'avensia-turqoise'}
            onClose={() => {
              setClickToExpandHintClicked(true);
              sessionStorage.setItem('clickToExpandHint', 'true');
            }}
          >
            <span>Click a section to expand it and get a list of included technologies</span>
          </Notification>
        )}
      </Flex>
    </Flex>
  );
}

export default Radar;
